import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "../pages/custom.css";
import { useLocation } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";

interface DocumentationViewerProps {
  filePath: string; // Ensure filePath is a string
  hideTableOnMobile?: boolean;
}

const DocumentationViewer: React.FC<DocumentationViewerProps> = ({
  filePath,
  hideTableOnMobile = false,
}) => {
  const isMobile = useMediaQuery("(max-width:768px)");
  const [markdownContent, setMarkdownContent] = useState<string>(""); // Set content state as string
  const [loading, setLoading] = useState<boolean>(true); // Loading state
  const location = useLocation(); // Get the current URL
  let skipNextHeading = false;
  const HeadingRenderer = (props: any) => {
    const children = React.Children.toArray(props.children);
    const text = children
      .reduce(
        (acc: any, child: any) =>
          acc + (child.props ? child.props.children : child),
        ""
      )
      .toString();
    if (hideTableOnMobile && isMobile && text === "Version Table") {
      return null;
    }
    // Generate ID by lowercasing the text and replacing spaces with hyphens
    const id = text.toLowerCase().replace(/\./g, "").replace(/\s+/g, "-");

    // Use the tag name from the node, which gives us the correct heading level (h1, h2, etc.)
    const tag = props.node.tagName || "h1"; // Default to "h1" if no tag is found

    return React.createElement(tag, { id }, props.children);
  };

  useEffect(() => {
    const fetchMarkdown = async () => {
      setLoading(true); // Set loading to true before fetching
      try {
        const response = await fetch(filePath);
        const text = await response.text();
        setMarkdownContent(text);
      } catch (error) {
        console.error("Error fetching the markdown file:", error);
      } finally {
        setLoading(false); // Set loading to false after fetching is complete
      }
    };

    fetchMarkdown();
  }, [filePath]);

  // Adjust the scroll position when the hash in the URL changes
  useEffect(() => {
    const handleHashChange = () => {
      if (location.hash) {
        const element = document.getElementById(location.hash.replace("#", ""));
        if (element) {
          const yOffset = -100; // Adjust this value to match the height of your fixed header
          const yPosition =
            element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: yPosition, behavior: "smooth" });
        }
      }
    };

    handleHashChange(); // Call initially to adjust if there's a hash in the URL

    window.addEventListener("hashchange", handleHashChange);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [location]);

  return (
    <div>
      <div className="kh-content-area">
        {loading ? (
          <div className="loading-spinner">
            {/* Replace with your spinner component or any loading indication */}
            <div className="spinner">Loading...</div>
          </div>
        ) : (
          <ReactMarkdown
            children={markdownContent}
            remarkPlugins={[remarkGfm]}
            components={{
              h1: HeadingRenderer,
              h2: HeadingRenderer,
              h3: HeadingRenderer,
              h4: HeadingRenderer,
              h5: HeadingRenderer,
              h6: HeadingRenderer,
              table: ({ node, ...props }) => {
                if (hideTableOnMobile && isMobile) {
                  return null; // hide the table
                }
                return <table {...props} className="w-full border-collapse" />;
              },
            }}
          />
        )}
      </div>
    </div>
  );
};

export default DocumentationViewer;
